"use client"

import { Link } from "react-router-dom"
import * as React from "react"
import Logo from "./Logo"
import { Facebook, Instagram, Linkedin } from "lucide-react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { useLanguageContext } from "../context/LanguageContext"

const DropdownMenu = DropdownMenuPrimitive.Root
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={`z-50 min-w-[8rem] overflow-hidden rounded-md border bg-white p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ${className}`}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={`relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-gray-100 ${className}`}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [dropdownOpen, setDropdownOpen] = React.useState(false)
  const [dropdownOpen2, setDropdownOpen2] = React.useState(false)
  const [languageDropdownOpen, setLanguageDropdownOpen] = React.useState(false)
  const [mobileLoginDropdownOpen, setMobileLoginDropdownOpen] = React.useState(false)
  const [mobilePartnerDropdownOpen, setMobilePartnerDropdownOpen] = React.useState(false)

  const { selectedLanguage, setSelectedLanguage } = useLanguageContext()

  // Replace this existing useEffect
  React.useEffect(() => {
    // We don't need to prevent scrolling for dropdown style menu
    // This was needed for the full-screen slide-in menu, but not for the dropdown
    document.body.style.overflow = "auto"

    // Cleanup function
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [isOpen])

  // With this improved version
  React.useEffect(() => {
    if (isOpen) {
      // Prevent scrolling when mobile menu is open
      document.body.style.overflow = "hidden"
    } else {
      // Allow scrolling when mobile menu is closed
      document.body.style.overflow = "auto"
    }

    // Cleanup function always restores scrolling
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [isOpen])

  const handleNavClick = (id) => {
    const currentPath = window.location.pathname
    if (currentPath === "/privacy-policy" || currentPath === "/terms-conditions") {
      const pathWithoutHash = "/"
      window.location.href = `${pathWithoutHash}#${id}`
    } else {
      window.history.pushState("", document.title, window.location.pathname + window.location.search)
    }

    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language)
    // localStorage is now handled in the context via useEffect
    setLanguageDropdownOpen(false)
    // window.location.reload();
  }
  const handleDropdownClose = () => {
    setDropdownOpen(false)
    setDropdownOpen2(false)
    setLanguageDropdownOpen(false)
    setMobileLoginDropdownOpen(false)
    setMobilePartnerDropdownOpen(false)
  }

  return (
    <nav className="fixed top-0 w-full bg-white z-50 border-b border-[#F0E6C8]">
      <div className="container mx-auto px-4 h-[72px] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link onClick={scrollToTop} to="/" className="flex items-center">
            <Logo compact className="h-9 sm:h-10 w-auto max-w-[200px]" />
          </Link>
        </div>

        <div className="h-[36.21px] w-[0.89px] bg-[#1E1E1E] opacity-50"></div>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to="#home"
            onClick={(e) => {
              e.preventDefault()
              handleNavClick("home")
              handleDropdownClose()
            }}
            className="text-sm text-brand-navy hover:text-brand-gold relative after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-0 after:h-[2px] after:bg-brand-gold hover:after:w-full after:transition-all"
          >
            Home
          </Link>
          <Link
            to="#usps"
            onClick={(e) => {
              e.preventDefault()
              handleNavClick("usps")
              handleDropdownClose()
            }}
            className="text-sm text-brand-navy hover:text-brand-gold relative after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-0 after:h-[2px] after:bg-brand-gold hover:after:w-full after:transition-all"
          >
            Our USPs
          </Link>
          <Link
            to="#about"
            onClick={(e) => {
              e.preventDefault()
              handleNavClick("about")
              handleDropdownClose()
            }}
            className="text-sm text-brand-navy hover:text-brand-gold relative after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-0 after:h-[2px] after:bg-brand-gold hover:after:w-full after:transition-all"
          >
            About Us
          </Link>
          <Link
            to="#how-it-works"
            onClick={(e) => {
              e.preventDefault()
              handleNavClick("how-it-works")
              handleDropdownClose()
            }}
            className="text-sm text-brand-navy hover:text-brand-gold relative after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-0 after:h-[2px] after:bg-brand-gold hover:after:w-full after:transition-all"
          >
            How It Works
          </Link>
          <DropdownMenu open={dropdownOpen2} onOpenChange={setDropdownOpen2}>
            <DropdownMenuTrigger asChild>
              <button
                className="text-sm text-brand-navy hover:text-brand-gold relative after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-0 after:h-[2px] after:bg-brand-gold hover:after:w-full after:transition-all"
                onClick={(e) => {
                  e.preventDefault()
                  setDropdownOpen2(!dropdownOpen2)
                }}
              >
                <div className="flex items-center gap-1">
                  Become a Partner
                  {dropdownOpen2 ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 30 30"
                      id="dropup-arrow"
                    >
                      <path d="M8.998 20h13.966c.286 0 .54-.138.72-.352l.004.004c.39-.434.39-1.138 0-1.572l-6.998-7.754a.926.926 0 0 0-1.41 0c-.01.012-.014.028-.024.04L8.272 18.08a1.202 1.202 0 0 0 0 1.572l.012.01a.945.945 0 0 0 .714.338z"></path>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 30 30"
                      id="dropdown-arrow"
                    >
                      <path d="M21.002 10H7.036c-.286 0-.54.138-.72.352l-.004-.004c-.39.434-.39 1.138 0 1.572l6.998 7.754c.35.39.925.39 1.274 0l6.984-7.776c.39-.434.39-1.138 0-1.572l-.012-.01a.945.945 0 0 0-.714-.338z"></path>
                    </svg>
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 mt-2">
               <DropdownMenuItem>
                <a
                  href="https://prod.egtogo.com/bpc-admin/signup/individual"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                BPC Admin 
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  href="https://prod.egtogo.com/signup/individual"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Business Partner
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  href="https://prod.egtogo.com/coordinator-signup/individual"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Business Partner Coordinator
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  href="https://play.google.com/store/apps/details?id=com.egtogo.partner&hl=en_IN"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Driver Partner
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  href="https://prod.egtogo.com/agent/signup/individual"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Agent
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button className="text-sm text-brand-navy hover:text-brand-gold relative after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-0 after:h-[2px] after:bg-brand-gold hover:after:w-full after:transition-all">
                <div className="flex items-center gap-1">
                  Login
                  {dropdownOpen ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 30 30"
                      id="dropup-arrow"
                    >
                      <path d="M8.998 20h13.966c.286 0 .54-.138.72-.352l.004.004c.39-.434.39-1.138 0-1.572l-6.998-7.754a.926.926 0 0 0-1.41 0c-.01.012-.014.028-.024.04L8.272 18.08a1.202 1.202 0 0 0 0 1.572l.012.01a.945.945 0 0 0 .714.338z"></path>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 30 30"
                      id="dropdown-arrow"
                    >
                      <path d="M21.002 10H7.036c-.286 0-.54.138-.72.352l-.004-.004c-.39.434-.39 1.138 0 1.572l6.998 7.754c.35.39.925.39 1.274 0l6.984-7.776c.39-.434.39-1.138 0-1.572l-.012-.01a.945.945 0 0 0-.714-.338z"></path>
                    </svg>
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 mt-2">
            <DropdownMenuItem>
                <a
                  href="https://prod.egtogo.com/bpc-admin/login"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Login as BPC Admin
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  href="https://prod.egtogo.com/coordinator/login"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Login as Business Partner Coordinator
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  href="https://prod.egtogo.com/login"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Login as Business Partner
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a
                  href="https://prod.egtogo.com/agent/login"
                  className="w-full py-2"
                  onClick={handleDropdownClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Login as Agent
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div>
          <DropdownMenu open={languageDropdownOpen} onOpenChange={setLanguageDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button className="text-sm text-brand-navy hover:text-brand-gold relative after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-0 after:h-[2px] after:bg-brand-gold hover:after:w-full after:transition-all">
                <div className="flex items-center gap-1">
                  {selectedLanguage === "EN" ? "Language" : selectedLanguage === "हिंदी" ? "भाषा" : selectedLanguage}
                  {languageDropdownOpen ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 30 30"
                      id="dropup-arrow"
                    >
                      <path d="M8.998 20h13.966c.286 0 .54-.138.72-.352l.004.004c.39-.434.39-1.138 0-1.572l-6.998-7.754a.926.926 0 0 0-1.41 0c-.01.012-.014.028-.024.04L8.272 18.08a1.202 1.202 0 0 0 0 1.572l.012.01a.945.945 0 0 0 .714.338z"></path>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 30 30"
                      id="dropdown-arrow"
                    >
                      <path d="M21.002 10H7.036c-.286 0-.54.138-.72.352l-.004-.004c-.39.434-.39 1.138 0 1.572l6.998 7.754c.35.39.925.39 1.274 0l6.984-7.776c.39-.434.39-1.138 0-1.572l-.012-.01a.945.945 0 0 0-.714-.338z"></path>
                    </svg>
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 mt-2">
              <DropdownMenuItem>
                <Link
                  href="/language/english"
                  className="w-full py-2"
                  onClick={() => {
                    handleLanguageChange("EN")
                    handleDropdownClose()
                  }}
                >
                  English
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Link
                  href="/language/hindi"
                  className="w-full py-2"
                  onClick={() => {
                    handleLanguageChange("हिंदी")
                    handleDropdownClose()
                  }}
                >
                  हिंदी
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center space-x-4">
            <a href="https://www.facebook.com/share/19f8ziRSbh/" target="_blank" rel="noopener noreferrer">
              <Facebook className="w-5 h-5 text-brand-navy hover:text-brand-gold transition-colors" />
            </a>
            <a href="https://www.instagram.com/egtogo?igsh=bmNkN3E4Nml1cXA4" target="_blank" rel="noopener noreferrer">
              <Instagram className="w-5 h-5 text-brand-navy hover:text-brand-gold transition-colors" />
            </a>
            <a href="https://www.linkedin.com/company/106875815/admin/dashboard/" target="_blank" rel="noopener noreferrer">
              <Linkedin className="w-5 h-5 text-brand-navy hover:text-brand-gold transition-colors" />
            </a>
            <a href="https://x.com/eg_go78464" target="_blank" rel="noopener noreferrer">
              <svg
                width="18"
                height="18"
                className="text-brand-navy hover:text-brand-gold transition-colors"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.4907 1.68384H19.4741L12.9563 9.13326L20.6239 19.2702H14.6202L9.91788 13.1222L4.53734 19.2702H1.55216L8.52359 11.3022L1.16797 1.68384H7.32411L11.5746 7.30337L16.4907 1.68384ZM15.4436 17.4845H17.0967L6.42585 3.37574H4.65188L15.4436 17.4845Z"
                  fill="#353535"
                />
              </svg>
            </a>
          </div>
          <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="fixed top-[72px] right-0 w-64 max-w-[90vw] bg-white shadow-lg z-50 rounded-bl-md overflow-hidden border border-[#F0E6C8]">
            <div className="flex flex-col max-h-[80vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <Link
                to="/"
                onClick={(e) => {
                  e.preventDefault()
                  setIsOpen(false)
                  window.location.href = "/"
                }}
                className="py-2 px-4 bg-brand-cream text-sm hover:bg-[#F6E9A8]"
              >
                Home
              </Link>
              <Link
                to="#about"
                onClick={(e) => {
                  e.preventDefault()
                  setIsOpen(false)
                  handleNavClick("about")
                }}
                className="py-2 px-4 text-sm hover:bg-brand-cream"
              >
                About
              </Link>
              <Link
                to="#how-it-works"
                onClick={(e) => {
                  e.preventDefault()
                  setIsOpen(false)
                  handleNavClick("how-it-works")
                }}
                className="py-2 px-4 text-sm hover:bg-brand-cream"
              >
                How it works
              </Link>
              <Link
                to="#usps"
                onClick={(e) => {
                  e.preventDefault()
                  setIsOpen(false)
                  handleNavClick("usps")
                }}
                className="py-2 px-4 text-sm hover:bg-brand-cream"
              >
                Our USPs
              </Link>
              {/* Become a Partner Mobile Dropdown */}
              <div className="relative">
                <button
                  className="py-2 px-4 text-sm hover:bg-brand-cream w-full text-left flex items-center justify-between"
                  onClick={() => setMobilePartnerDropdownOpen((prev) => !prev)}
                  aria-expanded={mobilePartnerDropdownOpen}
                  aria-controls="mobile-partner-dropdown"
                  type="button"
                >
                  <span>Become a Partner</span>
                  <span className="ml-2">
                    {mobilePartnerDropdownOpen ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 30 30"
                        id="dropup-arrow"
                      >
                        <path d="M8.998 20h13.966c.286 0 .54-.138.72-.352l.004.004c.39-.434.39-1.138 0-1.572l-6.998-7.754a.926.926 0 0 0-1.41 0c-.01.012-.014.028-.024.04L8.272 18.08a1.202 1.202 0 0 0 0 1.572l.012.01a.945.945 0 0 0 .714.338z"></path>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 30 30"
                        id="dropdown-arrow"
                      >
                        <path d="M21.002 10H7.036c-.286 0-.54.138-.72.352l-.004-.004c-.39.434-.39 1.138 0 1.572l6.998 7.754c.35.39.925.39 1.274 0l6.984-7.776c.39-.434.39-1.138 0-1.572l-.012-.01a.945.945 0 0 0-.714-.338z"></path>
                      </svg>
                    )}
                  </span>
                </button>
                {mobilePartnerDropdownOpen && (
                  <div
                    id="mobile-partner-dropdown"
                    className="absolute left-0 right-0 bg-white border rounded shadow-lg z-50 mt-1"
                  >
                     <a
                      href="https://prod.egtogo.com/bpc-admin/signup/individual"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      BPC Admin
                    </a>
                    <a
                      href="https://prod.egtogo.com/signup/individual"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Business Partner
                    </a>
                    <a
                      href="https://prod.egtogo.com/coordinator-signup/individual"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Business Partner Coordinator
                    </a>
                    <a
                        href="https://play.google.com/store/apps/details?id=com.egtogo.partner&hl=en_IN"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Driver Partner
                    </a>
                    <a
                      href="https://prod.egtogo.com/agent/signup/individual"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Agent
                    </a>
                  </div>
                )}
              </div>
              {/* Mobile Login Dropdown */}
              <div className="relative">
                <button
                  className="py-2 px-4 text-sm hover:bg-brand-cream w-full text-left flex items-center justify-between"
                  onClick={() => setMobileLoginDropdownOpen((prev) => !prev)}
                  aria-expanded={mobileLoginDropdownOpen}
                  aria-controls="mobile-login-dropdown"
                  type="button"
                >
                  <span>Login</span>
                  <span className="ml-2">
                    {mobileLoginDropdownOpen ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 30 30"
                        id="dropup-arrow"
                      >
                        <path d="M8.998 20h13.966c.286 0 .54-.138.72-.352l.004.004c.39-.434.39-1.138 0-1.572l-6.998-7.754a.926.926 0 0 0-1.41 0c-.01.012-.014.028-.024.04L8.272 18.08a1.202 1.202 0 0 0 0 1.572l.012.01a.945.945 0 0 0 .714.338z"></path>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 30 30"
                        id="dropdown-arrow"
                      >
                        <path d="M21.002 10H7.036c-.286 0-.54.138-.72.352l-.004-.004c-.39.434-.39 1.138 0 1.572l6.998 7.754c.35.39.925.39 1.274 0l6.984-7.776c.39-.434.39-1.138 0-1.572l-.012-.01a.945.945 0 0 0-.714-.338z"></path>
                      </svg>
                    )}
                  </span>
                </button>
                {mobileLoginDropdownOpen && (
                  <div
                    id="mobile-login-dropdown"
                    className="absolute left-0 right-0 bg-white border rounded shadow-lg z-50"
                  >
                       <a
                      href="https://prod.egtogo.com/bpc-admin/login"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Login as BPC Admin
                    </a>
                    <a
                      href="https://prod.egtogo.com/coordinator/login"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Login as Business Partner Coordinator
                    </a>
                    <a
                      href="https://prod.egtogo.com/login"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Login as Business Partner
                    </a>
                    <a
                      href="https://prod.egtogo.com/agent/login"
                      className="block px-4 py-2 text-sm hover:bg-brand-cream"
                      onClick={handleDropdownClose}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Login as Agent
                    </a>
                  </div>
                )}
              </div>
              <Link
                to="#faq"
                onClick={(e) => {
                  e.preventDefault()
                  setIsOpen(false)
                  handleNavClick("faq")
                }}
                className="py-2 px-4 text-sm hover:bg-brand-cream"
              >
                FAQ
              </Link>

              <div className="flex justify-center space-x-4 py-3 border-t">
                <a href="https://www.facebook.com/share/19f8ziRSbh/" target="_blank" rel="noopener noreferrer">
                  <Facebook className="w-5 h-5 text-brand-navy hover:text-brand-gold transition-colors" />
                </a>
                <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer">
                  <Linkedin className="w-5 h-5 text-brand-navy hover:text-brand-gold transition-colors" />
                </a>
                <a
                  href="https://www.instagram.com/egtogo?igsh=bmNkN3E4Nml1cXA4"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Instagram className="w-5 h-5 text-brand-navy hover:text-brand-gold transition-colors" />
                </a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer">
                  <svg
                    width="18"
                    height="18"
                    className="text-brand-navy hover:text-brand-gold transition-colors"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.4907 1.68384H19.4741L12.9563 9.13326L20.6239 19.2702H14.6202L9.91788 13.1222L4.53734 19.2702H1.55216L8.52359 11.3022L1.16797 1.68384H7.32411L11.5746 7.30337L16.4907 1.68384ZM15.4436 17.4845H17.0967L6.42585 3.37574H4.65188L15.4436 17.4845Z"
                      fill="#353535"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

export default Navbar
